import { useCallback, useContext, useEffect, useRef } from 'react'
import path from 'path'
import { AppContext, AppContextType, ScreenRecordingConversionProgress, ScreenRecordingInfo } from '../../AppContext'
import { UserContext, UserContextType } from '../../UserContext'
import ElectronIPC from '../../services/ElectronIPC'
import useRootSnackbar from '../../services/useRootSnackbar'
import { useDesktopSourceResolver } from '../../services/useDesktopSourceResolver'
import clone from 'clone'
import { getDesktopSources, MediaDeviceError } from '../../services/MediaDevices'
import { env } from '../../services/EnvironmentVariables'
import { logger } from '../../services/Logger'
import { extractErrorMessage } from '../../utils'
import { media_recorder_audio_bitrate } from '../../services/Constants'
import { RoomContext, RoomContextType } from '../../RoomContext'
import useAppContextRef from '../../services/useAppContextRef'
import useRoomContextRef from '../../services/useRoomContextRef'
import useUserContextRef from '../../services/useUserContextRef'
import { generateOutputFilename } from './screenRecordingLogic'
import { getAudioEngineInstance } from '../../core/AudioEngine'
import FakeMediaStreamTrack from '../../core/AudioEngine/FakeMediaStreamTrack'
import { useScreenRecordingConverter } from '../../services/useScreenRecordingConverter'
import { HybridAudioEngine } from '../../core/AudioEngine/HybridAudioEngine'
import { useTranslation } from 'react-i18next'
import { sessionSettingsTrigger } from '@/pages/SessionPage/SessionPage'

type FilePathInfo = {
    directory: string
    filenameBase: string
    filename: string
    fullPath: string
}

const log = logger.createChildLogger({ name: 'ScreenRecording' })

/**
 *
 * A) audio only
 *
 * B) window + audio
 *
 * C) desktop + audio
 *
 */
export function useScreenRecordingService() {
    const pathDelimiter = env.OS === 'win32' ? '\\' : '/'

    const { t } = useTranslation()

    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType
    const { state: roomState, dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType
    const { state } = useContext(UserContext) as UserContextType

    const { roomStateRef } = useRoomContextRef()
    const { userStateRef } = useUserContextRef()
    const { appStateRef } = useAppContextRef()

    const { convertScreenRecording } = useScreenRecordingConverter({ pathDelimiter })

    const { resolveDesktopSourceToMediaStream } = useDesktopSourceResolver()

    const { show: showSnackbar } = useRootSnackbar()

    const isScreenRecordingRef = useRef<ScreenRecordingInfo | null>(null)

    const mediaRecordingStreamRef = useRef<MediaStream | null>(null)
    const audioCtxRef = useRef<AudioContext>()
    const audioInputSourceNodesRef = useRef<Map<string, MediaStreamAudioSourceNode>>(new Map())
    const audioDestinationRef = useRef<MediaStreamAudioDestinationNode>()

    const setIsScreenRecording = useCallback((isScreenRecording: ScreenRecordingInfo | null) => {
        roomDispatch({
            type: 'update_room_context',
            value: {
                isScreenRecording: isScreenRecording,
            },
        })
    }, [])

    const setScreenRecordingConversionProgress = useCallback(
        (screenRecordingConversionProgress: ScreenRecordingConversionProgress | null) => {
            appDispatch({
                type: 'update_app_state',
                value: {
                    screenRecordingConversionProgress,
                },
            })
        },
        []
    )

    const onRecorderStopped = useCallback(() => {
        const lastRecordingInfo = clone(isScreenRecordingRef.current)
        log.checkpoint('screen_recording_end', '', { lastRecordingInfo })

        getAudioEngineInstance().stopMasterCaptureToFile()

        setIsScreenRecording(null)
        isScreenRecordingRef.current = null

        if (mediaRecordingStreamRef.current) {
            mediaRecordingStreamRef.current.getTracks().forEach((track) => {
                track.stop()
            })
            mediaRecordingStreamRef.current = null
        }

        if (lastRecordingInfo) {
            console.log(
                `screen-recording: calling convertWebm with parameters ` +
                    `lastRecordingInfo=${JSON.stringify(lastRecordingInfo)}`
            )
            setScreenRecordingConversionProgress({
                directory: lastRecordingInfo.outputFileDirectory,
                fileName: lastRecordingInfo.outputFilename,
                fullPath: lastRecordingInfo.outputFileFullPath,
                progress: 0, // not yet implemented
            })
            convertScreenRecording(
                lastRecordingInfo,
                // success
                (convertedFileInfo) => {
                    console.log(
                        `screen-recording: convertWebm returned success: convertedFileInfo=` +
                            JSON.stringify(convertedFileInfo)
                    )
                    // showSnackbar({
                    //     severity: 'success',
                    //     message: `Saved screen recording "${convertedFileInfo.outputFileFullPath}".`,
                    // })
                    appDispatch({
                        type: 'update_app_state',
                        value: {
                            showScreenRecordingConversionDialog: { convertedFileInfo },
                        },
                    })
                    log.checkpoint('screen_recording_end_success', '', {
                        lastRecordingInfo,
                    })
                    setScreenRecordingConversionProgress(null)
                },
                // error
                (code: number, message?: string) => {
                    console.log(`screen-recording: convertWebm returned failed: code=${code} message="${message}"`)
                    showSnackbar({
                        severity: 'error',
                        message: `Failed to convert to mp4. Message: "${message}".`,
                    })

                    log.checkpoint('e_screen_recording_ffmpeg', '', {
                        lastRecordingInfo,
                    })
                    setScreenRecordingConversionProgress(null)
                }
            )
        } else {
            console.error(`screen-recording: lastRecordingInfo is null! This should never happen.`)
        }
    }, [])

    // Used to throttle calls to start/stop recording by enforcing a refractory period
    // after each start/stop call.
    const refractoryTimerRef = useRef<NodeJS.Timeout | null>(null)
    const enterRefractoryPeriod = useCallback((type: 'start_recording' | 'stop_recording') => {
        const t = type === 'start_recording' ? 3000 : 2000

        refractoryTimerRef.current && clearTimeout(refractoryTimerRef.current)
        refractoryTimerRef.current = setTimeout(() => {
            refractoryTimerRef.current && clearTimeout(refractoryTimerRef.current)
            refractoryTimerRef.current = null
        }, t)
    }, [])

    const isInRefractoryPeriod = useCallback(() => {
        return refractoryTimerRef.current != null
    }, [])

    const stopRecording = useCallback((params?: { overrideRefractoryPeriod?: boolean }) => {
        const overrideRefractoryPeriod = params?.overrideRefractoryPeriod || false

        if (!overrideRefractoryPeriod && isInRefractoryPeriod()) {
            showSnackbar({
                severity: 'warning',
                message: t('Recorder is settling. Please try again in a moment.'),
            })
            return
        }

        enterRefractoryPeriod('stop_recording')

        if (recorder.current) {
            recorder.current.stop()
            recorder.current = null
        }
    }, [])

    const createStreamAndRecord = useCallback(async (stream: MediaStream, outputFileInfo: FilePathInfo) => {
        const f = outputFileInfo

        const mediaRecorderOptions = {
            mimeType: 'video/webm',
            audioBitsPerSecond: media_recorder_audio_bitrate,
        }

        console.log(`screen-recording: at createStreamAndRecord with stream=%O.`, stream)

        recorder.current = new MediaRecorder(stream, mediaRecorderOptions)
        recorder.current.ondataavailable = async (ev) => {
            const data = ev.data
            console.log(`screen-recording: ondataavailable data=<Blob size=${data.size.toLocaleString()}>`)

            const b = await ev.data.arrayBuffer()
            try {
                await ElectronIPC.saveAudioFileChunk({ uint8Array: new Uint8Array(b), filePath: f.fullPath })
            } catch (e) {
                console.error(e)

                showSnackbar({
                    severity: 'error',
                    message: t(`An error occurred while saving the recording to disk. Stopping recording.`),
                })

                if (isScreenRecordingRef.current) {
                    stopRecording({ overrideRefractoryPeriod: true })
                }
            }
        }

        recorder.current.onstop = onRecorderStopped
        recorder.current.start(1000) // arg: timeslice

        const info: ScreenRecordingInfo = {
            outputFilename: f.filename,
            outputFilenameBase: f.filenameBase,
            outputFileFullPath: f.fullPath,
            outputFileDirectory: f.directory,
            proAudioFileFullPath: path.join(f.directory, f.filenameBase + '-pro.mp3'),
            recordingStartedTime: new Date(),
        }
        setIsScreenRecording(info)
        isScreenRecordingRef.current = info

        getAudioEngineInstance().startMasterCaptureToFile(info.proAudioFileFullPath)
    }, [])

    const createNewRecording = useCallback(async (desktopStream: MediaStream | null, outputFileInfo: FilePathInfo) => {
        try {
            log.checkpoint('screen_recording_create', '', {
                program: desktopStream == null ? 'no_desktop' : 'builtin',
                outputFileInfo,
            })

            if (mediaRecordingStreamRef.current) {
                mediaRecordingStreamRef.current.getTracks().forEach((track) => {
                    track.stop()
                })
                mediaRecordingStreamRef.current = null
            }

            mediaRecordingStreamRef.current = new MediaStream()
            const stream = mediaRecordingStreamRef.current

            audioCtxRef.current = new AudioContext()
            audioDestinationRef.current = audioCtxRef.current.createMediaStreamDestination()

            audioInputSourceNodesRef.current = new Map()

            for (let a of userStateRef.current.mediaStreams.audioInputs) {
                if (a instanceof FakeMediaStreamTrack) {
                    continue
                }
                audioInputSourceNodesRef.current.set(
                    a.id,
                    audioCtxRef.current.createMediaStreamSource(new MediaStream([a]))
                )
                console.log(`screen-recording: adding audio track "${a.info.label}" from mediaStreams.audioInputs.`)
            }
            if (roomStateRef.current.currPeerAudioOutputStreamForScreenRecording instanceof MediaStream) {
                roomStateRef.current.currPeerAudioOutputStreamForScreenRecording.getAudioTracks().forEach((a) => {
                    console.log(
                        `screen-recording: adding audio track currPeerAudioOutputStreamForScreenRecording (label="${a.label}").`
                    )
                    audioInputSourceNodesRef.current.set(
                        a.id,
                        audioCtxRef.current!.createMediaStreamSource(new MediaStream([a]))
                    )
                })
            }
            if (desktopStream instanceof MediaStream) {
                desktopStream.getVideoTracks().forEach((v) => {
                    console.log(`screen-recording: adding video track mediaStreams.screenRecordDesktop.`)
                    stream.addTrack(v)
                })
            }

            const engine = getAudioEngineInstance()
            if (engine instanceof HybridAudioEngine) {
                const sourceNode = audioCtxRef.current!.createMediaStreamSource(engine.masterStreamNode.stream)
                audioInputSourceNodesRef.current.set('pro-audio', sourceNode)
            }

            for (let [id, source] of audioInputSourceNodesRef.current) {
                source.connect(audioDestinationRef.current)
            }

            console.log(`screen-recording: call createStreamAndRecord`)

            // if `audioInputSourceNodesRef.current.size === 0`, then MediaRecorder does not start.
            // we must add an empty audio source that just loops 0s. we can't just add the track
            // later when the user adds an audio device because that causes the recorder to die. Fixes #498.
            if (audioInputSourceNodesRef.current.size === 0) {
                const emptyBuffer = audioCtxRef.current.createBuffer(
                    1,
                    audioCtxRef.current.sampleRate,
                    audioCtxRef.current.sampleRate
                )

                // Fill the buffer with silence (zeros)
                for (let channel = 0; channel < emptyBuffer.numberOfChannels; ++channel) {
                    const bufferChannel = emptyBuffer.getChannelData(channel)
                    for (let i = 0; i < emptyBuffer.length; ++i) {
                        bufferChannel[i] = 0
                    }
                }

                // Create an AudioBufferSourceNode and connect it to the AudioContext's destination (output)
                const emptySource = audioCtxRef.current.createBufferSource()
                emptySource.buffer = emptyBuffer
                emptySource.loop = true
                emptySource.connect(audioDestinationRef.current)
            }

            const audioTrack = audioDestinationRef.current.stream.getAudioTracks()[0]
            if (audioTrack) {
                stream.addTrack(audioTrack)
            }

            await createStreamAndRecord(stream, outputFileInfo)
        } catch (e) {
            const ee = (e as any) || {}
            let message = extractErrorMessage(e)
            if (ee.code === 'directory_error') {
                showDirectoryErrorDialog(ee.path)
                return
            }

            showSnackbar({
                severity: 'error',
                message: t(`Failed to create recording! Error: {{message}}`, { message }),
            })
            log.error('createNewRecording', 'failed to create recording', { error: e })

            if (isScreenRecordingRef.current) {
                stopRecording({ overrideRefractoryPeriod: true })
            }
        }
    }, [])

    const showPermissionErrorDialog = useCallback((desktopStream: MediaDeviceError | null) => {
        appDispatch({
            type: 'new_message_dialog',
            value: {
                id: 'screen_recording_error',
                text:
                    `Failed to access the requested display. Please check that\n` +
                    `1) you have the correct screen recording permissions as required by your OS\n` +
                    `2) you are not recording a screen or a window that does not exist anymore.\n` +
                    `You need to restart the app in order for the permission changes to take effect.\n\n` +
                    `code: "${desktopStream?.name || 'undefined'}"\n` +
                    `message: "${desktopStream?.message || 'desktopStream is null.'}"`,
            },
        })
    }, [])

    const showDirectoryErrorDialog = useCallback((path: string) => {
        appDispatch({
            type: 'new_message_dialog',
            value: {
                id: 'screen_recording_directory_error',
                text:
                    `Could not write to "${path}".\n` +
                    `Please make sure that the directory exists and is writable, or choose a new directory to save the recording in Settings.\n` +
                    `You need to restart the app if you have updated the app's directory access permissions.`,
            },
        })
    }, [])

    const recorder = useRef<MediaRecorder | null>(null)
    const startRecording = useCallback(async (options: { openSettingsOnStreamError?: boolean }) => {
        try {
            console.log(`screen-recording: startRecording()`)
            if (isInRefractoryPeriod()) {
                console.log(`screen-recording: isInRefactoryPeriod() so no-op.`)
                return
            }

            enterRefractoryPeriod('start_recording')

            // throws { code: 'directory_error' } if given directory and fallback directories do not work.
            const f = await generateOutputFilename(appStateRef.current.appSettings.screenRecordSaveDirectory)
            console.log(`screen-recording: generated output filename "${f.fullPath}".`)

            if (f.isFallbackDirectory) {
                appDispatch({
                    type: 'update_app_settings',
                    value: { screenRecordSaveDirectory: f.directory },
                })
                showSnackbar({
                    severity: 'info',
                    message:
                        `Screen recording destination directory changed to "${f.directory}" ` +
                        `because the previously specified directory was not accessible.`,
                })
            }

            const screenRecordDesktopId: string | null = appStateRef.current.appSettings.screenRecordDesktop?.id || null
            if (screenRecordDesktopId === '_none') {
                console.log(`screen-recording: recording audio only because screenRecordDesktop is not selected.`)
                await createNewRecording(null, f) // screen record w/out video
            } else if (!screenRecordDesktopId || screenRecordDesktopId === '_undef') {
                sessionSettingsTrigger.trigger({ tab: 'recording' })
            } else {
                const fallbackDesktopSources = await getDesktopSources()

                const desktopStream = await resolveDesktopSourceToMediaStream(
                    screenRecordDesktopId,
                    appStateRef.current.appSettings.screenRecordVideoOptions || {},
                    fallbackDesktopSources,
                    appStateRef.current.appSettings.screenRecordDesktop?.label
                )

                console.log(
                    `screen-recording: resolve desktop "${screenRecordDesktopId}" (type=${typeof screenRecordDesktopId}) to MediaStream using ` +
                        `Electron's desktop capturer: ` +
                        `got desktopStream=%O`,
                    desktopStream
                )

                if (desktopStream instanceof MediaStream) {
                    await createNewRecording(desktopStream, f)
                } else if (!desktopStream || desktopStream instanceof MediaDeviceError) {
                    if (options?.openSettingsOnStreamError) {
                        sessionSettingsTrigger.trigger({ tab: 'recording' })
                        stopRecording({ overrideRefractoryPeriod: true })
                    } else {
                        showPermissionErrorDialog(desktopStream)
                    }
                }
            }
        } catch (e) {
            const ee = (e as any) || {}
            let message = extractErrorMessage(e)
            if (ee.code === 'directory_error') {
                showDirectoryErrorDialog(ee.path)
                return
            }

            showSnackbar({
                severity: 'error',
                message: `Failed to create recording! Error: ${message}`,
            })

            log.error('startRecording', 'failed to create recording', { error: e })

            if (isScreenRecordingRef.current) {
                stopRecording({ overrideRefractoryPeriod: true })
            }
        }
    }, [])

    useEffect(() => {
        if (!isScreenRecordingRef.current) {
            return
        }

        const audioCtx = audioCtxRef.current
        if (!audioCtx) {
            return
        }

        const audioDestination = audioDestinationRef.current
        if (!audioDestination) {
            return
        }

        const audioInputSources = audioInputSourceNodesRef.current
        if (!audioInputSources) {
            return
        }

        for (let a of state.mediaStreams.audioInputs) {
            if (a instanceof FakeMediaStreamTrack) {
                continue
            }
            // connect to audioDestination only if this track is already in audioInputSources.
            if (!audioInputSources.has(a.id)) {
                console.log(`screen-recording: adding audio track "${a.info.label}" from mediaStreams.audioInputs.`)
                let source = audioCtx.createMediaStreamSource(new MediaStream([a]))
                audioInputSources.set(a.id, source)
                source.connect(audioDestination)
            }
        }
        if (roomState.currPeerAudioOutputStreamForScreenRecording instanceof MediaStream) {
            roomState.currPeerAudioOutputStreamForScreenRecording.getAudioTracks().forEach((a) => {
                // connect to audioDestination only if this track is already in audioInputSources.
                if (!audioInputSources.has(a.id)) {
                    console.log(
                        `screen-recording: adding audio track currPeerAudioOutputStreamForScreenRecording (label="${a.label}").`
                    )
                    let source = audioCtx.createMediaStreamSource(new MediaStream([a]))
                    audioInputSources.set(a.id, source)
                    source.connect(audioDestination)
                }
            })
        }
    }, [roomState.currPeerAudioOutputStreamForScreenRecording, state.mediaStreams.audioInputs])

    useEffect(() => {
        if (roomState.screenRecordingCommand?.command === 'start') {
            startRecording(roomState.screenRecordingCommand)
        } else {
            if (recorder.current) {
                stopRecording()
            }
        }
    }, [roomState.screenRecordingCommand, startRecording, stopRecording])

    return {}
}
